/**
 * /dev/personalization — A3 verification panel.
 *
 * Proves:
 *   - Personalization controls bound to usePreferences() re-theme the page live.
 *   - Session demo (useSession()) shows currentUser + sign-in / sign-out.
 *   - Per-account prefs persist: change a pref while signed in, sign out,
 *     sign back in → the pref is restored (proves Part B boot-wiring).
 *
 * Dev-only scaffolding; removed at A-final cutover.
 */
import { useState } from 'react';
import styled from 'styled-components';
import { Button, Badge, ConsolePanel, Switch, Avatar } from '@/ui';
import { Icon } from '@/icons';
import { usePreferences } from '@/hooks/usePreferences';
import { useSession } from '@/hooks/useSession';
import type { AccentKey, TextSizeKey, BackdropKey } from '@/core/models';
import { PANEL_SOLIDITY_RANGE } from '@/core/models/preferences';

/* ─── Layout helpers ─────────────────────────────────────────────────────── */

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.color.bgRoot};
  padding: 32px 24px 80px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.display};
  color: ${({ theme }) => theme.color.textPrimary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 780px;
`;

const SectionHeading = styled.h2`
  margin: 0 0 16px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Code = styled.code`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ControlLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ControlName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textMuted};
`;

/** Colour swatch button for accent picker. */
const AccentSwatch = styled.button<{ $from: string; $to: string; $active: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ $from }) => $from};
  border: 3px solid
    ${({ $active, theme }) => ($active ? theme.color.textPrimary : 'transparent')};
  cursor: pointer;
  outline-offset: 3px;
  transition: border-color 0.15s;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
  }
`;

/** Generic segmented button row for text/radius pickers. */
const SegmentGroup = styled.div`
  display: flex;
  gap: 2px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  width: fit-content;
`;

const Segment = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  background: ${({ $active, theme }) => ($active ? theme.color.glassBorder : 'transparent')};
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ $active, theme }) =>
    $active ? theme.color.textPrimary : theme.color.textMuted};
  transition: background 0.12s, color 0.12s;
  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: -2px;
  }
`;

/** Range input styled to use the theme accent color. */
const RangeInput = styled.input`
  appearance: none;
  width: 200px;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.color.glassBorder};
  accent-color: ${({ theme }) => theme.color.brandFrom};
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 4px;
  }
`;

/** Anchor styled to look like a ghost button (for cross-panel navigation). */
const GhostLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  border: 1px solid transparent;
  background: transparent;
  transition: background 0.12s;
  &:hover {
    background: ${({ theme }) => theme.color.glassBg};
  }
`;

/** Info box for the session demo. */
const SessionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const SessionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SessionName = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
  font-weight: 600;
`;

const SessionHandle = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.accentMagenta};
  font-family: ${({ theme }) => theme.font.body};
`;

/* ─── Accent colours for the swatch picker ────────────────────────────────── */
// We only need the dark-mode "from" and "to" to render the swatches visually.
// Values come from the accent preset table (accents.ts) — just hardcoded here
// to avoid a runtime import of the full table into the panel.
const ACCENT_SWATCHES: Record<Exclude<AccentKey, 'custom'>, { from: string; to: string; label: string }> = {
  default:  { from: '#B57BFF', to: '#7C3AED', label: 'Purple' },
  teal:     { from: '#00FFB2', to: '#14B88A', label: 'Teal' },
  sunset:   { from: '#ff6b35', to: '#ff3d7f', label: 'Sunset' },
  aurora:   { from: '#00d4aa', to: '#00b4d8', label: 'Aurora' },
  ember:    { from: '#ff4500', to: '#f03645', label: 'Ember' },
  mint:     { from: '#00ff87', to: '#00e5cc', label: 'Mint' },
  magma:    { from: '#ff2d6b', to: '#b347ff', label: 'Magma' },
};

/* ─── Personalization section ───────────────────────────────────────────── */

function PersonalizationSection() {
  const { prefs, setPreference, themeMode, toggleTheme } = usePreferences();

  return (
    <ConsolePanel>
      <SectionHeading>Personalization: live re-theme</SectionHeading>
      <Column>

        {/* Theme toggle */}
        <ControlLabel>
          <ControlName>Theme</ControlName>
          <Row>
            <Switch
              checked={themeMode === 'dark'}
              onChange={() => toggleTheme()}
              label={themeMode === 'dark' ? 'Dark mode' : 'Light mode'}
            />
            <Code>{themeMode}</Code>
          </Row>
        </ControlLabel>

        {/* Accent picker */}
        <ControlLabel>
          <ControlName>Accent</ControlName>
          <Row>
            {(Object.entries(ACCENT_SWATCHES) as [AccentKey, { from: string; to: string; label: string }][]).map(
              ([key, { from, to, label }]) => (
                <div
                  key={key}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
                >
                  <AccentSwatch
                    type="button"
                    $from={from}
                    $to={to}
                    $active={prefs.accent === key}
                    aria-label={`Accent: ${label}`}
                    aria-pressed={prefs.accent === key}
                    onClick={() => void setPreference('accent', key)}
                  />
                  <Code style={{ fontSize: 9 }}>{label}</Code>
                </div>
              ),
            )}
          </Row>
          <Code>active: {prefs.accent}</Code>
        </ControlLabel>

        {/* The font picker that used to sit here changed nothing on screen — the
            type system is fixed in tokens.ts and applyPreferences never read
            fontFamily. A control wired to nothing is a bug; removed in 1.25.00. */}
        {/* Text size */}
        <ControlLabel>
          <ControlName>Text size</ControlName>
          <SegmentGroup role="group" aria-label="Text size">
            {(['sm', 'md', 'lg', 'xl'] as TextSizeKey[]).map((key) => (
              <Segment
                key={key}
                type="button"
                $active={prefs.textSize === key}
                aria-pressed={prefs.textSize === key}
                onClick={() => void setPreference('textSize', key)}
              >
                {key}
              </Segment>
            ))}
          </SegmentGroup>
          <Code>active: {prefs.textSize}</Code>
        </ControlLabel>

        {/* Radius */}
        <ControlLabel>
          <ControlName>Border radius</ControlName>
          {/* Radius is now a numeric px pref with user-editable slider bounds — the real
              control lives in Settings → Appearance → Card Appearance. Read-only here. */}
          <Code>
            radius: {prefs.radiusPx}px (bounds {prefs.radiusMinPx}–{prefs.radiusMaxPx})
          </Code>
        </ControlLabel>

        {/* Backdrop mode */}
        <ControlLabel>
          <ControlName>Backdrop</ControlName>
          <SegmentGroup role="group" aria-label="Backdrop mode">
            {(['animated', 'static', 'off'] as BackdropKey[]).map((key) => (
              <Segment
                key={key}
                type="button"
                $active={prefs.backdrop === key}
                aria-pressed={prefs.backdrop === key}
                onClick={() => void setPreference('backdrop', key)}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </Segment>
            ))}
          </SegmentGroup>
          <Code>active: {prefs.backdrop}</Code>
        </ControlLabel>

        {/* Panel transparency */}
        <ControlLabel>
          <ControlName>Panel transparency</ControlName>
          <RangeInput
            type="range"
            min={PANEL_SOLIDITY_RANGE.min}
            max={PANEL_SOLIDITY_RANGE.max}
            step={0.01}
            value={prefs.panelSolidity}
            aria-label="Panel transparency"
            aria-valuetext={`${Math.round(prefs.panelSolidity * 100)}% opacity`}
            onChange={(e) => void setPreference('panelSolidity', Number(e.target.value))}
          />
          <Code>opacity: {Math.round(prefs.panelSolidity * 100)}%</Code>
        </ControlLabel>

      </Column>
    </ConsolePanel>
  );
}

/* ─── Session demo ──────────────────────────────────────────────────────── */

function SessionSection() {
  const { currentUser, isAuthenticated, signIn, signOut } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSignIn() {
    setError(null);
    setBusy(true);
    try {
      await signIn('admin123', '1234abcd');
    } catch {
      setError('Sign-in failed. Check credentials or seed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ConsolePanel>
      <SectionHeading>Session: useSession() demo</SectionHeading>
      <Column>

        <SessionInfo>
          {isAuthenticated && currentUser ? (
            <>
              <Avatar name={currentUser.name} size={40} />
              <SessionText>
                <SessionName>{currentUser.name}</SessionName>
                <SessionHandle>{currentUser.handle}</SessionHandle>
              </SessionText>
              <span style={{ marginLeft: 'auto' }}><Badge tone="cyan">Signed in</Badge></span>
            </>
          ) : (
            <>
              <Icon name="users" size={24} title="No user" />
              <SessionText>
                <SessionName>Signed out</SessionName>
                <SessionHandle>No active session</SessionHandle>
              </SessionText>
              <span style={{ marginLeft: 'auto' }}><Badge tone="neutral">Guest</Badge></span>
            </>
          )}
        </SessionInfo>

        {error && <ErrorText>{error}</ErrorText>}

        <Row>
          {!isAuthenticated ? (
            <Button
              variant="primary"
              onClick={() => void handleSignIn()}
              disabled={busy}
            >
              <Icon name="check" size={16} />
              Sign in as admin123
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => signOut()}
            >
              <Icon name="close" size={16} />
              Sign out
            </Button>
          )}
        </Row>

        <Code>
          Per-account prefs: sign in, change accent/theme, sign out, sign back
          in, and the pref should be restored (proves Part B boot-wiring).
        </Code>
      </Column>
    </ConsolePanel>
  );
}

/* ─── Exported panel page ────────────────────────────────────────────────── */

export function PersonalizationPanel() {
  const { themeMode, toggleTheme } = usePreferences();

  return (
    <Page>
      <PageHeader>
        <PageTitle>
          <Icon name="sparkles" size={28} title="Personalization" />
          Personalization Panel
        </PageTitle>
        <Row>
          <Button variant="secondary" onClick={toggleTheme}>
            <Icon name={themeMode === 'dark' ? 'sun' : 'moon'} size={16} />
            {themeMode === 'dark' ? 'Light mode' : 'Dark mode'}
          </Button>
          <GhostLink href="/dev/components">
            <Icon name="chevron-right" size={16} />
            Component gallery
          </GhostLink>
        </Row>
      </PageHeader>

      <Sections>
        <PersonalizationSection />
        <SessionSection />
      </Sections>
    </Page>
  );
}
